import { CheckIcon, PencilIcon, TrashIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ChangeEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { deleteRoverTdm, patchRoverTdm } from '@/api/Rover/applicationTDM';
import type { IGetMilestonesResData, IPatchTdmPayload } from '@/api/Rover/applicationTDM/types';
import ImageCircularAvatar from '@/components/UI/Avatar/ImageCircularAvatar';
import Badge from '@/components/UI/Badge';
import Input from '@/components/UI/Form/Input';
import Select from '@/components/UI/Form/Select';
import Confirm from '@/components/UI/Modal/Confirm';
import { useApplicationTeamMembers } from '@/hooks/ReactQueryHooks/useApplicationQuery';
import { AppRoutes, buildRoute } from '@/route/routeConfig';
import type { GetVendorsResponse } from '@/types/Vendor';
import { milestoneStatus } from '../../../../../../../constants/Project';
import { TdmReactQueryKey } from '../../../../../../../constants/ReactQueryKeys';
import { useTitleById } from '../../../../../../../hooks/ReactQueryHooks/useTitlesQuery';
import { notifyUser } from '../../../../../../../store/Toast';
import type { TdmMemberInterface } from '../../../../../../../types/Tdm';
import { formatDate3, getInitials, isObjectEmpty, sanitizeResponseError } from '../../../../../../../utils';

interface MilestoneRowInterface extends IGetMilestonesResData {
  isEditing: boolean;
}

export default function DueDiligenceMilestoneRow({
  milestone,
  vendors,
}: {
  milestone: IGetMilestonesResData;
  vendors?: GetVendorsResponse[];
}) {
  const { applicationId, applicationFormId } = useParams();
  const navigate = useNavigate();
  const _location = useLocation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [inputErrors, setInputErrors] = useState({
    name: '',
    dueDate: '',
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [milestoneRow, setMilestoneRow] = useState<MilestoneRowInterface>({
    ...milestone,
    isEditing: false,
  });
  const [touchedInput, setTouchedInput] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    setMilestoneRow({ ...milestone, isEditing: false });
  }, [milestone]);

  const getApplicationTeamMembers = useApplicationTeamMembers({
    applicationId: applicationId!,
  });
  const applicationTeamMembers = getApplicationTeamMembers?.data?.data;
  const teams = applicationTeamMembers?.members?.map((member: any) => member?.user);

  const { getTitle } = useTitleById();

  const patchTdm = useMutation(
    (data: Partial<IPatchTdmPayload>) =>
      patchRoverTdm({
        tdmType: 'milestone',
        tdmId: milestoneRow.id,
        data,
      }),
    {
      onSuccess: () => {
        editRowHandler();
        queryClient.invalidateQueries([TdmReactQueryKey.GET_APPLICATION_MILESTONES]);
        setTouchedInput({});
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Milestone saved successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const errorLocation = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Milestone update failed. Invalid ${errorLocation}`,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const removeTdm = useMutation((tdmId: number) => deleteRoverTdm({ tdmType: 'milestone', tdmId }), {
    onSuccess: () => {
      queryClient.invalidateQueries([TdmReactQueryKey.GET_APPLICATION_MILESTONES]);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Milestone delete successful',
          toastType: 'success',
        })
      );
      setShowDeleteConfirm(false);
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const cancelEditHandler = () => {
    setMilestoneRow({ ...milestone, isEditing: false });
  };

  const editRowHandler = () => {
    setMilestoneRow((row: MilestoneRowInterface) => ({
      ...row,
      isEditing: !row.isEditing,
    }));
    if (!milestoneRow?.dueDate) {
      setInputErrors((prevState) => ({
        ...prevState,
        dueDate: 'Due Date is required',
      }));
    }
  };

  const rowInputChangeHandler = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    let newValue: any = e.target.value;

    if (e.target.name === 'lead' || e.target.name === 'support') {
      newValue = teams.find((mem: TdmMemberInterface) => mem.id === parseInt(newValue, 10));
    }

    if (e.target.name === 'vendor' && vendors) {
      const vendor = vendors?.find((mem) => mem.id === parseInt(newValue, 10));

      if (vendor) {
        newValue = vendor;
      }
    }

    if (e.target.name === 'startDate') {
      if (e.target.value.trim() === '') {
        setInputErrors((prevState) => ({
          ...prevState,
          startDate: 'Start Date is required',
        }));
      } else {
        setInputErrors((prevState) => ({
          ...prevState,
          startDate: '',
        }));
      }
    }

    if (e.target.name === 'dueDate') {
      if (e.target.value.trim() === '') {
        setInputErrors((prevState) => ({
          ...prevState,
          dueDate: 'Due Date is required',
        }));
      } else {
        setInputErrors((prevState) => ({
          ...prevState,
          dueDate: '',
        }));
      }
    }

    if (e.target.name === 'name') {
      if (e.target.value.trim() === '') {
        setInputErrors((prevState) => ({
          ...prevState,
          name: 'Milestone name is required',
        }));
      } else if (e.target.value.length > 250) {
        setInputErrors((prevState) => ({
          ...prevState,
          name: 'Must be less than 250 characters',
        }));
      } else {
        setInputErrors((prevState) => ({
          ...prevState,
          name: '',
        }));
      }
    }

    setTouchedInput((prevState) => ({
      ...prevState,
      [e.target.name]: true,
    }));
    setMilestoneRow((row: MilestoneRowInterface) => ({
      ...row,
      [e.target.name]: newValue,
    }));
  };

  const saveTdmHandler = () => {
    const data: Partial<IPatchTdmPayload> = {
      ...(touchedInput?.name && { name: milestoneRow.name }),
      ...(touchedInput?.status && { status: milestoneRow.status }),
      ...(touchedInput?.lead && milestoneRow.lead && { leadId: milestoneRow.lead.id }),
      ...(touchedInput?.support && milestoneRow.support && { supportId: milestoneRow.support.id }),
      ...(touchedInput?.vendor && milestoneRow.vendor && { vendorId: milestoneRow.vendor.id }),
      ...(touchedInput?.startDate && {
        startDate: milestoneRow.startDate ? formatDate3(milestoneRow.startDate) : '',
      }),
      ...(touchedInput?.dueDate && {
        dueDate: milestoneRow.dueDate ? formatDate3(milestoneRow.dueDate) : '',
      }),
    };

    if (inputErrors.name.length === 0 && inputErrors.dueDate.length === 0) {
      if (!isObjectEmpty(data)) {
        patchTdm.mutate(data);
      } else {
        editRowHandler();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'No changes were detected to save the milestone.',
            toastType: 'info',
          })
        );
      }
    }
  };

  const deleteMilestoneHandler = () => {
    setShowDeleteConfirm(true);
  };

  const deleteConfirmHandler = () => {
    removeTdm.mutate(milestoneRow.id);
  };

  const deleteCancelHandler = () => {
    setShowDeleteConfirm(false);
  };

  const tdmDetailViewHandler = () => {
    if (!applicationFormId || !applicationId) {
      return;
    }

    navigate(
      buildRoute(AppRoutes.ROVER_APPLICATION_FORM_APPLICATION_MILESTONE, {
        applicationFormId,
        applicationId,
        milestoneId: milestone.id,
      })
    );
  };

  return (
    <>
      {showDeleteConfirm && (
        <Confirm
          title={'Delete Milestone'}
          message={`You are about to delete ${milestoneRow?.name}. Are you sure you want to delete ?`}
          acceptButtonLabel={`${removeTdm.isLoading ? 'Deleting...' : 'Delete'}`}
          declineButtonLabel='Cancel'
          accept={deleteConfirmHandler}
          decline={deleteCancelHandler}
        />
      )}
      <tr
        key={milestone.id}
        className={'w-full flex justify-between items-center px-5 py-4 border-b border-gray-200 text-sm hover:bg-green-50 cursor-pointer'}
      >
        {!milestoneRow.isEditing && (
          <td className='w-72 flex items-start gap-2'>
            <div className='underline' onClick={tdmDetailViewHandler}>
              {milestoneRow.name}
            </div>
          </td>
        )}
        {milestoneRow.isEditing && (
          <td className='w-72 relative'>
            <Input type='text' defaultValue={milestoneRow.name} name='name' onChange={rowInputChangeHandler} />
            <span className='absolute text-xs text-tertiaryColor-normal'>{inputErrors?.name}</span>
          </td>
        )}
        <td className='w-36'>
          <div className=''>
            {!milestoneRow.isEditing && <Badge status={milestoneRow.status} text={milestoneStatus[milestoneRow.status]} />}
            {milestoneRow.isEditing && (
              <Select name='status' defaultValue={milestoneRow.status} onChange={rowInputChangeHandler}>
                {Object.keys(milestoneStatus)
                  .filter((s) => s !== 'UNDER_REVIEW' && s !== 'UNDER_REVISION' && s !== 'COMPLETE')
                  .map((key: string) => (
                    <option key={key} value={key}>
                      {milestoneStatus[key]}
                    </option>
                  ))}
              </Select>
            )}
          </div>
        </td>
        <td className='w-40'>
          {!milestoneRow.isEditing && milestoneRow.lead && (
            <Link to={`/app/administration/users/${milestoneRow?.lead?.id}`}>
              <div className='flex items-center gap-2'>
                <ImageCircularAvatar imageUrl={milestoneRow.lead.profileImageUrl} fallbackText={getInitials(milestoneRow.lead.name)} />
                <span>{milestoneRow.lead.name}</span>
              </div>
            </Link>
          )}
          {!milestoneRow.isEditing && !milestoneRow.lead && (
            <span>
              {milestoneRow.metaData.leadTitleId && getTitle(milestoneRow.metaData.leadTitleId)?.name}
              {milestoneRow.metaData.lead && milestoneRow.metaData.lead}
              {!milestoneRow.metaData.leadTitleId && !milestoneRow.metaData.lead && 'Unassigned'}
            </span>
          )}
          {milestoneRow.isEditing && (
            <Select name='lead' defaultValue={milestoneRow.lead ? milestoneRow.lead.id : ''} onChange={rowInputChangeHandler}>
              {teams?.map((user: TdmMemberInterface) => {
                if (user.id !== milestoneRow?.support?.id) {
                  return (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  );
                }
                return <></>;
              })}
            </Select>
          )}
        </td>
        <td className='w-40'>
          {!milestoneRow.isEditing && milestoneRow.support && (
            <Link to={`/app/administration/users/${milestoneRow?.support?.id}`}>
              <div className='flex items-center gap-2'>
                <ImageCircularAvatar
                  imageUrl={milestoneRow.support.profileImageUrl}
                  fallbackText={getInitials(milestoneRow.support.name)}
                />
                <span>{milestoneRow.support.name}</span>
              </div>
            </Link>
          )}
          {!milestoneRow.isEditing && !milestoneRow.support && (
            <span>
              {milestoneRow.metaData.supportTitleId && getTitle(milestoneRow.metaData.supportTitleId)?.name}
              {milestoneRow.metaData.support && milestoneRow.metaData.support}
              {!milestoneRow.metaData.supportTitleId && !milestoneRow.metaData.support && 'Unassigned'}
            </span>
          )}
          {milestoneRow.isEditing && (
            <Select name='support' defaultValue={milestoneRow.support ? milestoneRow.support.id : ''} onChange={rowInputChangeHandler}>
              {!milestoneRow.support && <option>Select Support</option>}
              {teams?.map((user: TdmMemberInterface) => {
                if (user.id !== milestoneRow?.lead?.id) {
                  return (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  );
                }
                return <></>;
              })}
            </Select>
          )}
        </td>
        <td className='w-40'>
          {!milestoneRow.isEditing && milestoneRow?.vendor && <Link to='/app/vendor'>{milestoneRow?.vendor?.name}</Link>}

          {!milestoneRow.isEditing && !milestoneRow?.vendor && 'Unassigned'}

          {milestoneRow.isEditing && (
            <Select name='vendor' defaultValue={milestoneRow?.vendor?.id} onChange={rowInputChangeHandler}>
              {!milestoneRow.vendor && <option>Select Vendor</option>}
              {vendors?.map((user) => {
                return (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                );
              })}
            </Select>
          )}
        </td>
        {!milestoneRow.isEditing && <td className={'w-24'}>{milestoneRow.dueDate ? formatDate3(milestoneRow.dueDate) : 'None'}</td>}
        {milestoneRow.isEditing && (
          <td className={'w-36 relative'}>
            <Input
              type='date'
              defaultValue={milestoneRow.dueDate && formatDate3(milestoneRow.dueDate, 'yyyy-mm-dd')}
              name='dueDate'
              min={formatDate3(milestoneRow.startDate, 'yyyy-mm-dd')}
              onChange={rowInputChangeHandler}
            />
            <span className='absolute text-xs text-tertiaryColor-normal'>{inputErrors?.dueDate}</span>
          </td>
        )}

        {!milestoneRow.isEditing && (
          <td className='w-24 flex gap-2 justify-center'>
            <div
              id='edit-button'
              className='rounded-full p-2 text-neutralColor-normal hover:bg-neutralColor-lighter hover:text-primaryColor-normal transition duration-300 ease-in-out'
              onClick={editRowHandler}
            >
              <PencilIcon className='h-5 w-5' />
            </div>
            <div
              id='delete-button'
              className='rounded-full p-2 text-neutralColor-normal hover:text-tertiaryColor-normal hover:bg-neutralColor-lighter transition duration-300 ease-in-out'
              onClick={deleteMilestoneHandler}
            >
              <TrashIcon className='h-5 w-5' />
            </div>
          </td>
        )}
        {milestoneRow.isEditing && (
          <td className='w-24 flex gap-4'>
            <div
              id='save-button'
              className='bg-gray-100 rounded-full p-2 text-primaryColor-normal hover:bg-primaryColor-lightest transition duration-300 ease-in-out'
              onClick={saveTdmHandler}
            >
              <CheckIcon className='h-5 w-5' />
            </div>
            <div
              id='cancel-button'
              className='bg-gray-100 rounded-full p-2 text-tertiaryColor-normal hover:bg-tertiaryColor-lightest transition duration-300 ease-in-out'
              onClick={cancelEditHandler}
            >
              <XCircleIcon className='h-5 w-5' />
            </div>
          </td>
        )}
      </tr>
    </>
  );
}
